import { getCenterViewSession, isCenterViewTab } from './utils';
import {
    decryptVerified,
    knownPlaintextFor,
    parseStoredWithExpiration,
    readAuthCipher,
    rememberVerifiedAuthCipher,
} from './authCipherMemo';

/**
 * 탭이 함께 쓰는 인증 저장소(localStorage 원문)를 **지금** 읽는다.
 *
 * react-secure-storage 의 getItem 은 탭이 시작될 때 localStorage 를 복호화해 올려 둔 **탭 메모리**를 돌려준다
 * (node_modules/react-secure-storage/dist/index.js — setItem 은 메모리와 해당 키 하나만 쓰고, 다른 탭의 변경은 메모리에
 * 반영되지 않는다). 그래서 다른 탭의 로그아웃·다른 계정 로그인·토큰 갱신을 보려면 원문을 직접 읽어야 한다.
 * 이 방식은 상품 분류 일괄 적용의 세션 확인(scenes/ClassPass/SettlementCategory/bulkFlowSession.ts)에서 먼저 쓰던 것을
 * 공용으로 옮긴 것이다.
 *
 * - 읽기에 앱의 getItemWithExpiration 을 쓰지 않는다 — 만료 시 값을 지우고 location.replace 를 부르는 부작용이 있다.
 *   여기서는 만료면 null 만 돌려준다.
 * - 센터 열람 탭: 세션이 그 탭의 sessionStorage 한 곳뿐이라(다른 탭과 공유하지 않는다) 그 값을 돌려준다.
 * - 복호화 모듈을 못 읽으면 null(fail-closed — 「같은 세션」으로 보지 않는다).
 */

/*
 * 복호화(읽을 때마다 새 복호화기 — 4회차 검증 P2)·저장 형식 검사·검증된 암호문 짝 기억은 lib/authCipherMemo 에 있다.
 * 복호화 실패를 탭 메모리 값으로 대신하지 않는다 — 실패하면 암호문이 이 탭이 검증해 아는 것과 완전히 같을 때만 그 평문
 * (저장 뒤 지문이 바뀐 경우, 5·6회차 검증), 아니면 null.
 */
export { parseStoredWithExpiration };

/** 공유 localStorage 에서 키 하나의 원문(복호화한 문자열). 없거나 못 읽으면 null. */
export const readSharedStoredRaw = (name: string): string | null => {
    try {
        const cipher = readAuthCipher(name);
        if (!cipher) return null;
        const plaintext = decryptVerified(cipher);
        if (plaintext) {
            // (c) 같은 암호문 문자열을 복호화·검증했다 — 이 짝을 기억한다.
            rememberVerifiedAuthCipher(name, cipher, plaintext);
            return plaintext;
        }
        // 복호화 실패·형식 아님: 기존 짝은 덮지 않고, 암호문이 검증해 아는 것과 완전히 같을 때만 그 평문. 아니면 null.
        return knownPlaintextFor(name, cipher);
    } catch {
        return null;
    }
};

/**
 * 인증 값(authorization·authObject)의 **지금** 공유 값. 없거나 만료면 null. 센터 열람 탭이면 그 탭의 세션 값.
 */
export const readSharedAuthValue = (
    name: 'authorization' | 'authObject' | 'refreshToken',
    now: number = Date.now(),
): any => {
    if (isCenterViewTab()) {
        // 열람 탭은 갱신 토큰을 쓰지 않는다(getItemWithExpiration 과 같은 규칙).
        if (name === 'refreshToken') return null;
        const session = getCenterViewSession();
        if (!session || !(Number(session.expirationTime) > now)) return null;
        return session[name] ?? null;
    }
    const stored = parseStoredWithExpiration(readSharedStoredRaw(name));
    if (!stored || stored.expirationTime <= now) return null;
    return stored.value ?? null;
};
